import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import type { FacilitiesAdminApi } from '../../facilities/index.js'

/** The admin calls this screen uses. */
type AdminApi = FacilitiesAdminApi

/** How many of a client's facilities show before asking for more, and how many each "Show more" adds. */
export const FACILITIES_SHOWN_FIRST = 5
export const FACILITIES_SHOWN_MORE = 25

/**
 * One client's facilities for the admin area, in name order and loaded a
 * page at a time: a few to begin with, more on request, narrowed by
 * `search` to names starting with it. Shared by the client's own panel and
 * the all-facilities page, on one cache key, so a client's facilities are
 * fetched once whichever is opened first - and under the key the
 * whole-list readers use, so anything that invalidates a client's
 * facilities refreshes these too.
 */
export function useClientFacilityPages(api: AdminApi, clientId: string, search: string) {
  const q = search.trim()
  const query = useInfiniteQuery({
    queryKey: ['admin', 'facilities', clientId, 'pages', q],
    queryFn: ({ pageParam }) => api.listFacilitiesPage(clientId, { limit: pageParam === null ? FACILITIES_SHOWN_FIRST : FACILITIES_SHOWN_MORE, cursor: pageParam, q }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    placeholderData: keepPreviousData,
  })
  const facilities = query.data?.pages.flatMap((page) => page.items) ?? []
  return {
    query,
    facilities,
    /** How many the client has, or how many match the search: the total the first page reports. */
    matching: query.data?.pages[0]?.total ?? facilities.length,
    isSearching: q !== '',
  }
}
